import { CONSENT_KEYS, consentTexts, CONSENT_VERSION, REQUIRED_CONSENTS, type ConsentKey } from "@kmstories/shared";
import { useId, useState } from "react";
import { useT } from "../i18n/LocaleProvider.js";

export type ConsentState = Record<ConsentKey, boolean>;

export const EMPTY_CONSENTS: ConsentState = {
  storageReview: false,
  ageConfirmed: false,
  filmLicense: false,
  webExcerpts: false,
  futureContact: false,
  updates: false,
};

/**
 * GDPR-granular consent as UX: every box unticked, two marked required, the
 * rest visibly optional. One human sentence beside the box, the full legal
 * text one tap away. The film licence being separate and optional is itself a
 * trust signal — the form demonstrates that submitting is not surrendering.
 */
export function ConsentBlock({ value, onChange, showError }: { value: ConsentState; onChange: (v: ConsentState) => void; showError?: boolean }) {
  const { m, locale } = useT();
  const texts = consentTexts(CONSENT_VERSION, locale);
  const id = useId();
  return (
    <fieldset className="form" style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="field__label" style={{ marginBlockEnd: "0.5rem" }}>
        {m.share.consentHeading}
      </legend>
      <p className="field__hint">{m.share.consentIntro}</p>
      {CONSENT_KEYS.map((key) => (
        <ConsentRow
          key={key}
          id={`${id}-${key}`}
          required={REQUIRED_CONSENTS.includes(key)}
          checked={value[key]}
          summary={texts[key].summary}
          legal={texts[key].legal}
          invalid={Boolean(showError) && REQUIRED_CONSENTS.includes(key) && !value[key]}
          onChange={(checked) => onChange({ ...value, [key]: checked })}
        />
      ))}
      {showError && REQUIRED_CONSENTS.some((k) => !value[k]) && (
        <p className="field__error" role="alert">
          {m.share.errors.consentRequired}
        </p>
      )}
    </fieldset>
  );
}

function ConsentRow({ id, required, checked, summary, legal, invalid, onChange }: { id: string; required: boolean; checked: boolean; summary: string; legal: string; invalid: boolean; onChange: (v: boolean) => void }) {
  const { m } = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="check" style={invalid ? { borderColor: "var(--saffron)" } : undefined}>
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-describedby={`${id}-legal`} aria-invalid={invalid || undefined} />
      <div className="check__body">
        <span className={`check__tag ${required ? "" : "check__tag--optional"}`}>{required ? m.share.required : m.share.optional}</span>
        <label htmlFor={id}>{summary}</label>
        <button type="button" className="linkish" aria-expanded={open} aria-controls={`${id}-legal`} onClick={() => setOpen((o) => !o)}>
          {open ? m.share.hideFull : m.share.readFull}
        </button>
      </div>
      <div id={`${id}-legal`} className="check__legal" hidden={!open}>
        {legal}
      </div>
    </div>
  );
}
